import { useCallback, useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../components/ConfirmDialog";
import { fetchCategories, createCategory, deleteCategory } from "../api/categories";

const sortByName = (a, b) => {
  const nameA = (a?.name || "").toLowerCase();
  const nameB = (b?.name || "").toLowerCase();
  return nameA.localeCompare(nameB);
};

function CategoriesPage({ onNotify }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [formError, setFormError] = useState("");
  const [creating, setCreating] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [query, setQuery] = useState("");
  const [confirmingCategory, setConfirmingCategory] = useState(null);
  const [deletingId, setDeletingId] = useState("");

  const getCategoryId = useCallback((category, fallbackIndex = 0) => {
    if (!category || typeof category !== "object") return `category-${fallbackIndex}`;
    return category.id || category._id || category.key || category.slug || category.name || `category-${fallbackIndex}`;
  }, []);

  const normalizeCategory = useCallback(
    (category, fallbackIndex) => {
      const id = getCategoryId(category, fallbackIndex);
      const count =
        category?.productCount ??
        category?.productsCount ??
        category?.products ??
        category?.count ??
        category?.items ??
        null;
      return {
        ...category,
        id,
        name: category?.name || "Untitled category",
        productCount: typeof count === "number" ? count : undefined,
      };
    },
    [getCategoryId]
  );

  const setCategoryList = useCallback(
    (list) => {
      const normalized = list.map((item, index) => normalizeCategory(item, index));
      normalized.sort(sortByName);
      setCategories(normalized);
    },
    [normalizeCategory]
  );

  const upsertCategory = useCallback(
    (category) => {
      setCategories((prev) => {
        const normalized = normalizeCategory(category, prev.length);
        const next = prev.some((item) => item.id === normalized.id)
          ? prev.map((item) => (item.id === normalized.id ? normalized : item))
          : [...prev, normalized];
        return [...next].sort(sortByName);
      });
    },
    [normalizeCategory]
  );

  const loadCategories = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await fetchCategories();
      const list = Array.isArray(response) ? response : [];
      setCategoryList(list);
    } catch (err) {
      console.error("Unable to load categories", err);
      setCategories([]);
      setLoadError(err?.message || "Unable to load categories.");
    } finally {
      setLoading(false);
    }
  }, [setCategoryList]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const filteredCategories = useMemo(() => {
    const value = query.trim().toLowerCase();
    if (!value) return categories;
    return categories.filter((category) => category.name?.toLowerCase().includes(value));
  }, [categories, query]);

  const formatDate = useCallback((value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }, []);

  const getInitials = (name) => {
    if (!name) return "CT";
    return name
      .split(" ")
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const handleCreateCategory = useCallback(
    async (event) => {
      event.preventDefault();
      if (creating) return;
      const name = nameInput.trim();
      if (!name) {
        setFormError("Enter a category name before saving.");
        return;
      }
      setFormError("");
      setCreating(true);
      try {
        const category = await createCategory({ name });
        upsertCategory(category);
        setNameInput("");
        if (onNotify) {
          onNotify(`Category “${category?.name || name}” saved.`, "success");
        }
      } catch (err) {
        console.error("Unable to create category", err);
        const message = err?.message || "Unable to create category.";
        setFormError(message);
        if (onNotify) {
          onNotify(message, "danger");
        }
      } finally {
        setCreating(false);
      }
    },
    [creating, nameInput, onNotify, upsertCategory]
  );

  const handleDeleteCategory = useCallback(async () => {
    if (!confirmingCategory?.id) return;
    const categoryId = confirmingCategory.id;
    setDeletingId(categoryId);
    try {
      await deleteCategory(categoryId);
      setCategories((prev) => prev.filter((cat) => cat.id !== categoryId));
      if (onNotify) {
        onNotify(`Category “${confirmingCategory.name || "Category"}” deleted.`, "success");
      }
      setConfirmingCategory(null);
    } catch (err) {
      console.error("Unable to delete category", err);
      const message = err?.message || "Unable to delete category.";
      if (onNotify) {
        onNotify(message, "danger");
      }
    } finally {
      setDeletingId("");
    }
  }, [confirmingCategory, onNotify]);

  const showingCount = filteredCategories.length;
  const totalCount = categories.length;

  return (
    <>
      <div className="categories-page">
        <section className="panel categories-panel">
          <div className="categories-toolbar">
            <div className="categories-top-row">
              <div className="categories-summary-card">
                <div className="muted extra-small">Total categories</div>
                <div className="categories-summary-value">{totalCount.toLocaleString()}</div>
                <p className="muted extra-small">Synced with the catalog</p>
              </div>
              <div className="categories-add">
                <form className="category-form" onSubmit={handleCreateCategory}>
                  <div className="field">
                    <label className="field-label" htmlFor="category-name">
                      Category name
                    </label>
                    <div className="input-with-action">
                      <input
                        id="category-name"
                        type="text"
                        placeholder="e.g. Orthopedic Implants"
                        value={nameInput}
                        onChange={(event) => setNameInput(event?.target?.value || "")}
                        disabled={creating}
                      />
                      <button className="primary-btn" type="submit" disabled={creating || !nameInput.trim()}>
                        {creating ? "Saving…" : "Add category"}
                      </button>
                    </div>
                  </div>
                  {formError ? <p className="form-error">{formError}</p> : null}
                </form>
              </div>
            </div>
            <div className="categories-search">
              <div className="table-search">
                <div className="table-search-inner">
                  <span className="search-icon" aria-hidden="true">
                    🔍
                  </span>
                  <input
                    type="search"
                    placeholder="Search categories"
                    aria-label="Search categories"
                    value={query}
                    onChange={(event) => setQuery(event?.target?.value || "")}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="muted extra-small categories-hint">
            Showing {showingCount.toLocaleString()} of {totalCount.toLocaleString()} categories
          </div>

          {loadError ? (
            <div className="inline-alert error">
              <div>{loadError}</div>
              <button className="ghost-btn subtle small" type="button" onClick={loadCategories} disabled={loading}>
                Retry
              </button>
            </div>
          ) : null}

          {loading ? (
            <div className="categories-empty">
              <p className="muted">Loading categories…</p>
            </div>
          ) : filteredCategories.length ? (
            <ul className="categories-list">
              {filteredCategories.map((category) => {
                const updatedLabel = formatDate(category.updatedAt) || formatDate(category.createdAt);
                const productCount = category.productCount;
                return (
                  <li key={category.id} className="category-card">
                    <div className="category-avatar" aria-hidden="true">
                      {getInitials(category.name)}
                    </div>
                    <div className="category-content">
                      <div className="category-name">{category.name}</div>
                      <div className="category-meta muted extra-small">
                        {updatedLabel ? `Updated ${updatedLabel}` : "Created from inventory"}
                      </div>
                    </div>
                    <div className="category-stats">
                      {productCount !== undefined ? (
                        <span className="pill subtle">
                          {`${productCount} ${productCount === 1 ? "Product" : "Products"}`}
                        </span>
                      ) : null}
                      <button
                        className="category-delete-btn icon-btn"
                        type="button"
                        title={`Delete ${category.name}`}
                        aria-label={`Delete ${category.name}`}
                        aria-busy={deletingId === category.id}
                        disabled={deletingId === category.id}
                        onClick={() => setConfirmingCategory(category)}
                      >
                        {deletingId === category.id ? (
                          <span className="category-delete-spinner" aria-hidden="true">
                            …
                          </span>
                        ) : (
                          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 3.5a1 1 0 011-1h4a1 1 0 011 1V5h3.5a1 1 0 110 2h-.94l-.8 12.13A2 2 0 0113.78 21H10.2a2 2 0 01-1.99-1.87L7.4 7H6a1 1 0 110-2H9V3.5zm2 1.5v0.99h2V5h-2zm-1.07 4a1 1 0 011 .94l.38 6.5a1 1 0 11-2 .12l-.38-6.5a1 1 0 01.94-1.06zm4.22 0a1 1 0 01.94 1.06l-.38 6.5a1 1 0 11-2-.12l.38-6.5a1 1 0 011-.94z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="categories-empty">
              <p className="muted">No categories match that search.</p>
            </div>
          )}
        </section>
      </div>

      <ConfirmDialog
        open={Boolean(confirmingCategory)}
        title="Delete category?"
        message={
          confirmingCategory?.name
            ? `Delete “${confirmingCategory.name}”? This will also remove the category from any products using it.`
            : "Delete this category? This will also remove the category from any products using it."
        }
        confirmLabel="Delete category"
        cancelLabel="Keep category"
        loading={deletingId === confirmingCategory?.id}
        onCancel={() => {
          if (deletingId === confirmingCategory?.id) return;
          setConfirmingCategory(null);
        }}
        onConfirm={handleDeleteCategory}
      />
    </>
  );
}

export default CategoriesPage;
